// @react
import React from 'react'
// @libraries
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
// @components
import Divider from '@bit/atd.web.divider'
import Grid from '@bit/atd.web.grid'
import Button from '@bit/atd.web.button'
import Box from '@bit/atd.web.box'
import strings from 'lib/constants/strings'

type PropsType = {
  handleMemberOnClick?: (
    tier?: 'professional' | 'professional-plus' | 'enterprise',
    type?:
      | 'join-now'
      | 'learn-more'
      | 'select-student'
      | 'select-senior'
      | 'select-professional',
  ) => void
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    border: '1px solid #C10013',
    boxShadow: '0px 2px 0px #C10013',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  cardDivider: {
    position: 'relative',
    padding: '24px 0',
  },
  cardLabel: {
    color: '#C10013',
    fontSize: '11px',
    fontWeight: 600,
    lineHeight: '16px',
    marginBottom: '8px',
    textTransform: 'uppercase',
  },
  cardTitle: {
    color: '#2E2D31',
    fontSize: '24px',
    lineHeight: '32px',
    marginBottom: '24px',
  },
  cardTitleNoLabel: {
    [theme.breakpoints.up('sm')]: {
      marginTop: '24px',
    },
  },
  cardPrice: {
    color: '#2E2D31',
    fontSize: '48px',
    fontWeight: 600,
    lineHeight: '38px',
    marginBottom: '32px',
    '& span': {
      color: '#424140',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '16px',
    },
  },
  cardActionButtons: {
    '& button:last-child': {
      marginLeft: '8px',
    },
  },
  cardTitleList: {
    color: '#424140',
    fontWeight: 500,
    lineHeight: '22px',
    marginBottom: '16px',
  },
  cardList: {
    color: '#424140',
    flex: '1 0 auto',
    padding: 0,
    margin: 0,
    '& li': {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '16px',
      '&:last-child': {
        marginBottom: '0',
      },
    },
    '& svg': {
      marginRight: '8px',
      display: 'inline-block',
      lineHeight: 1,
      flexShrink: 0,
      maxWidth: 'initial',
    },
    '& span': {
      fontWeight: 500,
    },
  },
  cardTitleRates: {
    color: '#2E2D31',
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: '16px',
  },
  cardRatesPlan: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: '#2E2D31',
  },
  cardRatesPrice: {
    color: '#2E2D31',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '34px',
    marginBottom: '8px',
    '& span': {
      color: '#424140',
      display: 'block',
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
    },
  },
  cardMoreCaption: {
    paddingLeft: '35px',
    color: 'grey',
  },
}))

const Index = ({ handleMemberOnClick }: PropsType) => {
  const classes = useStyles()

  const handleOnClick = (type?: any) => {
    if (handleMemberOnClick) handleMemberOnClick('professional-plus', type)
  }

  return (
    <Box
      classNames={{
        root: classes.root,
        body: classes.body,
      }}
    >
      <Typography className={classes.cardLabel} component="p">
        {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.POPULAR}
      </Typography>
      <Typography className={classes.cardTitle} component="h3">
        {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.TITLE}
      </Typography>
      <Typography className={classes.cardPrice} component="p">
        {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.PRICE}{' '}
        <span>
          {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.PRICE_LABEL}
        </span>
      </Typography>
      <div className={classes.cardActionButtons}>
        <Button
          size="small"
          variant="primary"
          onClick={() => handleOnClick('join-now')}
        >
          {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.BUTTON_ONE}
        </Button>
        <Button
          size="small"
          variant="tertiary"
          onClick={() => handleOnClick('learn-more')}
        >
          {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.BUTTON_TWO}
        </Button>
      </div>

      <div className={classes.cardDivider}>
        <Divider width="100%" color="#DBD9D6" height="1px" />
      </div>

      <Typography className={classes.cardTitleList} component="p">
        {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.FEATURE_TEXT}
      </Typography>
      <ul className={classes.cardList}>
        <li>
          <IconCheckmark />
          <span>
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.FEATURES
                .FEATURE_MAG_PLUS
            }
          </span>
        </li>
        <li>
          <IconCheckmark />
          <span>
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.FEATURES
                .FEATURE_LIB_PLUS
            }
          </span>
        </li>
        <li>
          <IconCheckmark />
          <span>
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.FEATURES
                .FEATURE_GUIDE_PLUS
            }
          </span>
        </li>
        <li>
          <IconCheckmark />
          <span>
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.FEATURES
                .FEATURE_MICRO_PLUS
            }
          </span>
        </li>
        <li>
          <span className={classes.cardMoreCaption}>
            {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.TEXT}
          </span>
        </li>
      </ul>

      <div className={classes.cardDivider}>
        <Divider width="100%" color="#DBD9D6" height="1px" />
      </div>

      <Typography className={classes.cardTitleRates} component="p">
        {strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATE_TITLE}
      </Typography>
      <Grid container alignItems={'flex-end'}>
        <Grid item xs={4} style={{ textAlign: 'center' }}>
          <Typography className={classes.cardRatesPlan} component="p">
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.STUDENTS
                .TITLE
            }
          </Typography>
          <Typography className={classes.cardRatesPrice} component="p">
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.STUDENTS
                .PRICE
            }{' '}
            <span>
              {
                strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.STUDENTS
                  .PRICE_LABEL
              }
            </span>
          </Typography>
          <Button
            size="small"
            variant="secondary"
            onClick={() => handleOnClick('select-student')}
          >
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.STUDENTS
                .BUTTON
            }
          </Button>
        </Grid>

        <Grid item xs={4} style={{ textAlign: 'center' }}>
          <Typography className={classes.cardRatesPlan} component="p">
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.SENIORS
                .TITLE
            }
          </Typography>
          <Typography className={classes.cardRatesPrice} component="p">
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.SENIORS
                .PRICE
            }{' '}
            <span>
              {
                strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.SENIORS
                  .PRICE_LABEL
              }
            </span>
          </Typography>
          <Button
            size="small"
            variant="secondary"
            onClick={() => handleOnClick('select-senior')}
          >
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.SENIORS
                .BUTTON
            }
          </Button>
        </Grid>

        <Grid item xs={4} style={{ textAlign: 'center' }}>
          <Typography className={classes.cardRatesPlan} component="p">
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.YOUNG_PRO
                .TITLE
            }
          </Typography>
          <Typography className={classes.cardRatesPrice} component="p">
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.YOUNG_PRO
                .PRICE
            }{' '}
            <span>
              {
                strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.YOUNG_PRO
                  .PRICE_LABEL
              }
            </span>
          </Typography>
          <Button
            size="small"
            variant="secondary"
            onClick={() => handleOnClick('select-professional')}
          >
            {
              strings.ONBOARDING.MEMBERSHIP.PROFESSIONAL_PLUS.RATES.YOUNG_PRO
                .BUTTON
            }
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Index

function IconCheckmark() {
  return (
    <svg width={24} height={24} fill="none">
      <circle cx={12} cy={12} r={11} stroke="#424140" strokeWidth={2} />
      <path
        d="M6.75 12.815l3.652 3.196L17.25 8.25"
        stroke="#424140"
        strokeWidth={2}
        strokeMiterlimit={10}
      />
    </svg>
  )
}
